import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import {
  type BodyTextVisibleRange,
  findVisibleIframe,
  getVisibleIframesTextWithRange,
} from '../../helpers/readium';
import {
  type ChunkRange,
  getChunkRanges,
  normalizeForChunking,
  textChunker,
} from '../../helpers/text-chunker';
import { MetadataService } from '../metadata/metadata';
import { PaymentStatusService } from '../payment-status/payment-status';
import { ReaderNavigatorService } from '../readium/navigator/reader-navigator';

type PlaybackState = 'idle' | 'playing' | 'paused';

interface TtsChunk {
  text: string;
  percentage: number;
}

interface CurrentText {
  start: string;
  highlight: string;
  end: string;
}

@Injectable({
  providedIn: 'root',
})
export class TtsService {
  private static readonly NEXT_PREV_DEBOUNCE = 400;
  private static readonly PERCENTAGE_MULTIPLIER = 100;
  private static readonly BOUNDARY_WORD_SPLIT = /[¿?¡!(){}[\];,:./\s]/;
  private static readonly MIN_HIGHLIGHT_WORD_LENGTH = 1;
  private static readonly LANGUAGE_PREFIX_LENGTH = 2;
  private static readonly MAX_INVISIBLE_PAGE_TURNS = 2;
  private static readonly MAX_VISIBLE_IFRAME_SETTLE_RETRIES = 30;
  private static readonly EMPTY_TEXT: CurrentText = { start: '', highlight: '', end: '' };
  private static readonly STOP_RETRY_DELAY = 50;

  private readonly metadataService = inject(MetadataService);
  private readonly readerNavigatorService = inject(ReaderNavigatorService);
  private readonly paymentStatusService = inject(PaymentStatusService);

  private readonly onVoicesChangedHandler = this.loadVoices.bind(this);

  private chunks: TtsChunk[] = [];
  private chunkRanges: ChunkRange[] = [];
  private chunkIndex = 0;
  private chunksIframe: Window | undefined;
  private utterance: SpeechSynthesisUtterance | null = null;
  private nextPrevDisabled = false;
  private invisiblePageTurnAttempts = 0;
  private operationGeneration = 0;
  private navigationDirection: 1 | -1 = 1;

  readonly isDialogOpen = signal<boolean>(false);
  readonly availableVoices = signal<SpeechSynthesisVoice[]>([]);
  readonly groupedVoices = computed(() =>
    Object.groupBy(this.availableVoices(), ({ lang }) => lang),
  );
  readonly selectedVoice = signal<SpeechSynthesisVoice | null>(null);
  readonly selectedRate = signal<number>(1);
  readonly playbackState = signal<PlaybackState>('idle');
  readonly currentText = signal<CurrentText>(TtsService.EMPTY_TEXT);
  readonly progress = signal<number>(0);
  readonly isSupported = computed(() => 'speechSynthesis' in window);
  readonly hasAvailableVoices = computed(() => this.availableVoices().length > 0);

  constructor() {
    // Auto page turns keep calling next() behind the blocking dialog, so the voice would
    // carry on reading the same page while the reader is blocked.
    effect(() => {
      if (this.paymentStatusService.isBlocked()) this.pause();
    });

    effect(() => {
      const metadata = this.metadataService.metadata();

      if (!metadata) {
        return;
      }

      // The speechSynthesis is idle, and we call it this first time to wake it up.
      this.loadVoices();

      // Once it is awake, we need to wait for voices load to retrieve all the available voices
      speechSynthesis.removeEventListener('voiceschanged', this.onVoicesChangedHandler);
      speechSynthesis.addEventListener('voiceschanged', this.onVoicesChangedHandler);
    });

    // actualLocator changes on every page turn (same-chapter or chapter transition — see
    // ReaderNavigatorService.positionChanged), unlike currentWindowIframe which only changes
    // on a chapter transition. This is the equivalent of the original's PAGINATION_CHANGED
    // listener that resumes reading once the page we asked for has finished turning.
    effect(() => {
      this.readerNavigatorService.actualLocator();

      // untracked: only react to page turns, not to every playbackState change start() causes.
      if (untracked(() => this.playbackState()) !== 'playing') {
        return;
      }

      void this.start();
    });

    // Auto-play as soon as the dialog opens, so the user doesn't have to hit play manually.
    effect(() => {
      if (this.isDialogOpen() && untracked(() => this.playbackState()) === 'idle') {
        void this.start();
      }
    });
  }

  loadVoices(): void {
    const metadata = this.metadataService.metadata();

    if (!metadata) {
      this.availableVoices.set([]);
      return;
    }

    const { ttsLanguage } = metadata.config;
    const languagePrefix = ttsLanguage.substring(0, TtsService.LANGUAGE_PREFIX_LENGTH);
    const voices = speechSynthesis
      .getVoices()
      .filter((voice) => voice.lang.includes(languagePrefix));
    this.availableVoices.set(voices);
    // Prefer a voice matching the full region (e.g. "es-ES"); fall back to any voice for the language.
    this.selectedVoice.set(voices.find((voice) => voice.lang === ttsLanguage) ?? voices[0]);
  }

  async start(): Promise<void> {
    const currentIframe = this.readerNavigatorService.currentWindowIframe();

    if (!currentIframe) {
      return;
    }

    if (
      this.playbackState() === 'playing' &&
      this.chunksIframe === currentIframe &&
      speechSynthesis.speaking
    ) {
      return;
    }

    speechSynthesis.cancel();

    // Rebuild whenever the iframe itself changed too, not just when chunks were reset: the
    // continuation effect can fire while a chapter transition is still settling (Readium may
    // correct the position with an async 'go_progression' message after the locator already
    // updated), so a rebuild triggered by that intermediate state must not get "stuck" reading
    // the old chapter's stale chunks once the real new iframe arrives.
    if (this.chunks.length === 0 || this.chunksIframe !== currentIframe) {
      const generation = ++this.operationGeneration;
      this.chunksIframe = currentIframe;

      await this.waitForVisibleIframeToMatch(currentIframe);
      if (generation !== this.operationGeneration) {
        // A newer page turn (or another start() call) has already taken over meanwhile; that
        // call owns chunks/chunkIndex now, so don't clobber it with this stale one's result.
        return;
      }

      const visibleRange = getVisibleIframesTextWithRange(
        this.readerNavigatorService.getCurrentFrameElements(),
      );
      this.chunks = textChunker(visibleRange.text);
      this.chunkRanges = getChunkRanges(normalizeForChunking(visibleRange.text), this.chunks);

      const firstVisibleIndex = TtsService.locateChunkInRange(this.chunkRanges, visibleRange);
      this.chunkIndex =
        firstVisibleIndex === -1
          ? this.navigationDirection === -1
            ? this.chunks.length - 1
            : 0
          : firstVisibleIndex;
      this.invisiblePageTurnAttempts = 0;
    }

    this.playbackState.set('playing');
    this.speak();
  }

  pause(): void {
    this.operationGeneration++;
    this.detachUtteranceHandlers();
    this.stopSpeaking();
    this.playbackState.set('paused');
  }

  resume(): void {
    void this.start();
  }

  cancel(): void {
    this.operationGeneration++;
    this.detachUtteranceHandlers();
    this.stopSpeaking();
    this.resetChunks();
    this.invisiblePageTurnAttempts = 0;
    this.playbackState.set('idle');
    this.currentText.set(TtsService.EMPTY_TEXT);
    this.progress.set(0);
    this.isDialogOpen.set(false);
  }

  next(): void {
    this.stepChunk(1);
  }

  prev(): void {
    this.stepChunk(-1);
  }

  private stepChunk(direction: 1 | -1): void {
    if (this.nextPrevDisabled || this.chunks.length === 0) {
      return;
    }

    this.nextPrevDisabled = true;
    setTimeout(() => {
      this.nextPrevDisabled = false;
    }, TtsService.NEXT_PREV_DEBOUNCE);

    const wasPlaying = this.playbackState() === 'playing';
    this.detachUtteranceHandlers();
    this.stopSpeaking();
    this.invisiblePageTurnAttempts = 0;
    this.navigationDirection = direction;

    const nextIndex = this.chunkIndex + direction;

    if (wasPlaying) {
      this.chunkIndex = nextIndex;
      this.playbackState.set('playing');
      this.speak();
    } else {
      this.chunkIndex = Math.min(Math.max(nextIndex, 0), this.chunks.length - 1);
      this.playbackState.set('paused');
      this.writeChunk();
    }
  }

  private detachUtteranceHandlers(): void {
    if (!this.utterance) {
      return;
    }

    this.utterance.onend = null;
    this.utterance.onboundary = null;
  }

  private stopSpeaking(): void {
    const stoppedUtterance = this.utterance;
    speechSynthesis.cancel();

    setTimeout(() => {
      if (this.utterance === stoppedUtterance && speechSynthesis.speaking) {
        speechSynthesis.cancel();
      }
    }, TtsService.STOP_RETRY_DELAY);
  }

  private findVisibleChunkIndex(): number {
    const frames = this.readerNavigatorService.getCurrentFrameElements();
    return TtsService.locateChunkInRange(this.chunkRanges, getVisibleIframesTextWithRange(frames));
  }

  private isChunkVisible(index: number): boolean {
    const range = this.chunkRanges[index];
    const frames = this.readerNavigatorService.getCurrentFrameElements();
    const { text, visibleStart, visibleEnd } = getVisibleIframesTextWithRange(frames);
    const start = TtsService.mapRawOffsetToNormalized(text, visibleStart);
    const end = TtsService.mapRawOffsetToNormalized(text, visibleEnd);
    return TtsService.rangeOverlaps(range, start, end);
  }

  private static locateChunkInRange(ranges: ChunkRange[], visible: BodyTextVisibleRange): number {
    const start = TtsService.mapRawOffsetToNormalized(visible.text, visible.visibleStart);
    const end = TtsService.mapRawOffsetToNormalized(visible.text, visible.visibleEnd);
    return ranges.findIndex((range) => TtsService.rangeOverlaps(range, start, end));
  }

  private static mapRawOffsetToNormalized(rawText: string, rawOffset: number): number {
    return rawOffset === -1 ? -1 : normalizeForChunking(rawText.slice(0, rawOffset)).length;
  }

  private static rangeOverlaps(range: ChunkRange, start: number, end: number): boolean {
    return start !== -1 && range.end > start && range.start < end;
  }

  private speak(): void {
    if (this.chunkIndex === this.chunks.length) {
      this.resetChunks();
      this.navigationDirection = 1;
      this.turnPage();
      return;
    }

    if (this.chunkIndex < 0) {
      this.resetChunks();
      this.navigationDirection = -1;
      this.turnPage();
      return;
    }

    if (!this.writeChunk()) {
      return;
    }

    const chunk = this.chunks[this.chunkIndex];
    const selectedVoice = this.selectedVoice();
    const utterance = new SpeechSynthesisUtterance(chunk.text);
    this.utterance = utterance;

    if (selectedVoice) {
      utterance.voice = selectedVoice;
      utterance.lang = selectedVoice.lang;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    (utterance as unknown as { voiceURI: string }).voiceURI = 'native';

    utterance.rate = this.selectedRate();
    utterance.onend = (): void => {
      this.chunkIndex++;
      this.navigationDirection = 1;
      this.speak();
    };
    utterance.onboundary = (event): void => {
      this.onBoundary(event, chunk);
    };

    speechSynthesis.speak(utterance);
  }

  private onBoundary(event: SpeechSynthesisEvent, chunk: TtsChunk): void {
    const { utterance } = this;

    if (!utterance) {
      return;
    }

    const [word] = utterance.text.substring(event.charIndex).split(TtsService.BOUNDARY_WORD_SPLIT);

    if (word.length <= TtsService.MIN_HIGHLIGHT_WORD_LENGTH) {
      return;
    }

    const charIndex = Math.max(event.charIndex - 1, 0);
    const wordIndex = chunk.text.substring(charIndex).indexOf(word);

    if (wordIndex === -1) {
      return;
    }

    const index = wordIndex + charIndex;
    this.changeText({
      start: chunk.text.substring(0, index),
      highlight: chunk.text.substring(index, index + word.length),
      end: chunk.text.substring(index + word.length),
      percentage: chunk.percentage,
    });
  }

  private writeChunk(): boolean {
    if (this.invisiblePageTurnAttempts > 0) {
      const visibleIndex = this.findVisibleChunkIndex();
      const movedInNavigationDirection =
        this.navigationDirection === 1
          ? visibleIndex > this.chunkIndex
          : visibleIndex < this.chunkIndex;

      if (visibleIndex !== -1 && movedInNavigationDirection) {
        this.chunkIndex = visibleIndex;
      }
    }

    const chunk = this.chunks[this.chunkIndex];
    this.changeText({ start: chunk.text, highlight: '', end: '', percentage: chunk.percentage });

    if (
      !this.isChunkVisible(this.chunkIndex) &&
      this.invisiblePageTurnAttempts < TtsService.MAX_INVISIBLE_PAGE_TURNS
    ) {
      this.invisiblePageTurnAttempts++;
      this.turnPage();
      return false;
    }

    this.invisiblePageTurnAttempts = 0;
    return true;
  }

  private async waitForVisibleIframeToMatch(target: Window): Promise<void> {
    for (let attempt = 0; attempt < TtsService.MAX_VISIBLE_IFRAME_SETTLE_RETRIES; attempt++) {
      if (
        findVisibleIframe(this.readerNavigatorService.getCurrentFrameElements())?.contentWindow ===
        target
      ) {
        return;
      }
      await TtsService.waitForNextPaint();
    }
  }

  private static async waitForNextPaint(): Promise<void> {
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          resolve();
        });
      });
    });
  }

  private turnPage(): void {
    if (this.navigationDirection === -1) {
      this.readerNavigatorService.prev();
    } else {
      this.readerNavigatorService.next();
    }
  }

  private resetChunks(): void {
    this.chunks = [];
    this.chunkRanges = [];
    this.chunkIndex = 0;
    this.chunksIframe = undefined;
  }

  private changeText(text: CurrentText & { percentage: number }): void {
    const { start, highlight, end, percentage } = text;
    this.currentText.set({ start, highlight, end });
    this.progress.set(percentage * TtsService.PERCENTAGE_MULTIPLIER);
  }
}
